import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Grid,
  ToggleButtonGroup,
  ToggleButton,
  Button,
  Skeleton,
} from "@mui/material";
import { Servers, Grid as GridIcon, Rows, Filter } from "@mynaui/icons-react";
import AssetCard from "../../dashboard/components/AssetCard";
import AssetFilters from "../components/AssetFilters";
import Pagination from "../components/Pagination";
import Footer from "../../../shared/components/Footer";
import { useZone } from "../../../shared/context/ZoneContext";
import assetService from "../../../services/assetService";

const ITEMS_PER_PAGE = 10;

const getRiskColor = (level) => {
  switch (level?.toLowerCase()) {
    case "critical":
      return "#DC2626";
    case "high":
      return "#EA580C";
    case "medium":
      return "#D97706";
    case "low":
      return "#16A34A";
    default:
      return "#6B7280";
  }
};

const getRulColor = (days) => {
  if (days <= 30) return "#DC2626";
  if (days <= 90) return "#D97706";
  return "#16A34A";
};

const mapAsset = (asset) => ({
  id: asset.id,
  name: asset.name,
  tag: asset.tag,
  image: asset.imageUrl,
  issue: asset.latestAlertMessage,
  status: asset.status,
  riskLevel: asset.riskLevel
    ? asset.riskLevel.charAt(0).toUpperCase() + asset.riskLevel.slice(1)
    : asset.riskLevel,
  riskColor: getRiskColor(asset.riskLevel),
  rul: { value: asset.rulDays != null ? `${asset.rulDays} days` : "N/A" },
  rulDays: asset.rulDays,
  rulColor: getRulColor(asset.rulDays),
  healthScore: asset.healthScore,
  failuresLastMonth: asset.failuresLastMonth,
  lastChecked: asset.lastCheckedAt
    ? new Date(asset.lastCheckedAt).toLocaleDateString()
    : "N/A",
});

const AssetsPage = () => {
  const { selectedZone } = useZone();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const [showFilters, setShowFilters] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    criticality: "All Criticalities",
    technician: "All Technicians",
    healthScoreMin: "",
    healthScoreMax: "",
    rulMin: "",
    rulMax: "",
  });

  useEffect(() => {
    if (searchQuery.length > 0 && searchQuery.length < 3) return;
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    if (!selectedZone) return;
    setLoading(true);
    assetService
      .getAssets(selectedZone, debouncedSearch || undefined)
      .then((res) => {
        const data = res?.data?.data ?? [];
        setAssets(data.map(mapAsset));
      })
      .catch(() => setAssets([]))
      .finally(() => setLoading(false));
  }, [selectedZone, debouncedSearch]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };

  const filteredAssets = useMemo(() => {
    return assets.filter((asset) => {
      if (filters.criticality !== "All Criticalities" && asset.riskLevel !== filters.criticality) return false;
      if (filters.healthScoreMin !== "" && asset.healthScore < Number(filters.healthScoreMin)) return false;
      if (filters.healthScoreMax !== "" && asset.healthScore > Number(filters.healthScoreMax)) return false;
      if (filters.rulMin !== "" && (asset.rulDays == null || asset.rulDays < Number(filters.rulMin))) return false;
      if (filters.rulMax !== "" && (asset.rulDays == null || asset.rulDays > Number(filters.rulMax))) return false;
      return true;
    });
  }, [assets, filters.criticality, filters.healthScoreMin, filters.healthScoreMax, filters.rulMin, filters.rulMax]);

  const totalPages = Math.ceil(filteredAssets.length / ITEMS_PER_PAGE);
  const paginatedAssets = filteredAssets.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  return (
    <Box sx={{ minHeight: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header Row */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#fff",
          px: { xs: 2, md: 4 },
          py: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: showFilters ? 1 : 0,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Servers size={24} color="#020618" strokeWidth={1.5} />
            <Typography
              variant="h4"
              sx={{
                fontWeight: 500,
                fontSize: "18px",
                lineHeight: "28px",
                color: "#111827",
              }}
            >
              Assets
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {/* Search */}
            <TextField
              placeholder="Search"
              size="small"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              sx={{
                width: { xs: "100%", sm: 300, md: 448 },
                maxWidth: 448,
                "& .MuiOutlinedInput-root": {
                  height: 40,
                  borderRadius: "8px",
                  bgcolor: "#FFFFFF",
                  "& fieldset": { borderColor: "#E2E8F0" },
                },
                "& .MuiOutlinedInput-input": { fontSize: "0.875rem" },
              }}
            />

            {/* View Toggle */}
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, newMode) => newMode && setViewMode(newMode)}
              size="small"
              sx={{
                gap: 1,
                "& .MuiToggleButton-root": {
                  border: "1px solid #E5E7EB",
                  borderRadius: "8px !important",
                  px: 1.25,
                  py: 0.75,
                  "&.Mui-selected": {
                    bgcolor: "#0F172B",
                    borderRadius: "8px",
                    color: "#fff",
                    "&:hover": { bgcolor: "#1B2A3D" },
                  },
                },
              }}
            >
              <ToggleButton value="grid">
                <GridIcon size={20} />
              </ToggleButton>
              <ToggleButton value="list">
                <Rows size={20} />
              </ToggleButton>
            </ToggleButtonGroup>

            {/* Filters Button */}
            <Button
              variant="contained"
              disableElevation
              startIcon={<Filter size={18} />}
              onClick={() => setShowFilters((prev) => !prev)}
              sx={{
                bgcolor: "#020618",
                color: "#F1F5F9",
                fontWeight: 500,
                fontSize: "14px",
                lineHeight: "20px",
                padding: "8px 16px",
                borderRadius: "8px",
                "&:hover": { bgcolor: "#1B2A3D" },
              }}
            >
              Filters
            </Button>
          </Box>
        </Box>

        {/* Filters */}
        {showFilters && (
          <AssetFilters filters={filters} onFilterChange={handleFilterChange} />
        )}
      </Box>

      <Box sx={{ px: { xs: 2, md: 4 }, py: 3, flexGrow: 1 }}>
        {/* Asset Cards Grid / Skeleton */}
        <Grid container spacing={2.5}>
          {loading
            ? Array.from({ length: ITEMS_PER_PAGE }).map((_, i) => (
                <Grid size={{ xs: 12, md: viewMode === "grid" ? 6 : 12 }} key={i}>
                  <Box
                    sx={{
                      border: "1px solid #E2E8F0",
                      borderRadius: "12px",
                      p: 2.5,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Skeleton variant="rectangular" width={56} height={56} sx={{ borderRadius: "8px" }} />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton variant="text" width="60%" height={22} />
                        <Skeleton variant="text" width="40%" height={18} />
                      </Box>
                      <Skeleton variant="rounded" width={72} height={24} />
                    </Box>
                    <Skeleton variant="text" width="90%" height={18} />
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <Skeleton variant="text" width="30%" height={18} />
                      <Skeleton variant="text" width="30%" height={18} />
                      <Skeleton variant="text" width="30%" height={18} />
                    </Box>
                  </Box>
                </Grid>
              ))
            : paginatedAssets.map((asset) => (
                <Grid
                  size={{ xs: 12, md: viewMode === "grid" ? 6 : 12 }}
                  key={asset.id}
                >
                  <AssetCard asset={asset} />
                </Grid>
              ))}
        </Grid>

        {/* Empty State */}
        {!loading && filteredAssets.length === 0 && (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Typography variant="h6" sx={{ color: "text.secondary", mb: 1 }}>
              No assets found
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Try adjusting your search or filter criteria
            </Typography>
          </Box>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalItems={filteredAssets.length}
            itemsPerPage={ITEMS_PER_PAGE}
          />
        )}
      </Box>

      <Footer />
    </Box>
  );
};

export default AssetsPage;
